import * as Dropzone from 'dropzone'; // dropzone could not be updated to 5.9.3, see commit 3d5983f8cd4600ed8b02cc17ac1134160f07d43d -> got Uncaught TypeError: dropzone__WEBPACK_IMPORTED_MODULE_0__ is not a constructor buildUploadComponent fileUpload.ts; looks like this issue: https://github.com/dropzone/dropzone/issues/1903)
import * as Sortable from 'sortablejs';
import { postRequestAndWaitForJobCompletion, waitForJobCompletion, disableElementAndAddSpinner } from './jobCompletion';
import { ResourceConstants } from './_generated/ResourceConstants';

export const filesCountChangedEventName = "filesCountChanged";

interface FileInfo extends Dropzone.DropzoneFile {
    serverFileId: number;
    copyright: string;
    existingFile?: boolean;
    serverFileUrl: string;
    serverFileSize?: number;
}

interface AcceptableFile {
    acceptFile: () => void;
    rejectFile: (reason: string) => void;
}

interface Dimensions {
    width: number;
    height: number;
}

interface ServerFileInfo {
    Id: number;
    Copyright?: string;
    Url?: string;
    Size?: number;
}

type FileType = 'image' | 'audio' | 'video';

const maxImageWidth = 20000;
const maxImageHeight= 20000;

export function buildUploadComponent(uploaderEl: JQuery<HTMLElement>) {
    uploaderEl.addClass('dropzone');
    const mode = uploaderEl.attr("data-mode") as FileType || 'image';
    const resourceApiUrl = uploaderEl.attr("data-url");
    const jobApiUrl = uploaderEl.attr("data-job-url")!;
    const aggregateRootEntityName = uploaderEl.attr('data-aggregate-root-entity-name');
    const maxFiles = parseInt(uploaderEl.attr("data-max-files") || '5');
    const maxFileSize = parseInt(uploaderEl.attr("data-max-file-size") || '30');
    const defaultMessage = uploaderEl.attr("data-message");
    const minImageWidth = parseInt(uploaderEl.attr("data-min-width") || '1');
    const minImageHeight = parseInt(uploaderEl.attr("data-min-height") || '1');
    const acceptedFiles = uploaderEl.attr("data-accepted-files") || ResourceConstants.supportedImageMimeTypes;

    const fileInfoEl = $('#' + uploaderEl.attr("data-fileinfo-input"));
    const existingFileData = fileInfoEl.val() as string || '[]';
    let parsedExistingFileData = JSON.parse(existingFileData);
    var isFileCollection = true;
    if (!Array.isArray(parsedExistingFileData)) {
        isFileCollection = false;
        parsedExistingFileData = parsedExistingFileData !== null ? [parsedExistingFileData] : [];
    }
    let existingFiles = parsedExistingFileData as ServerFileInfo[];

    const dropZoneOptions: Dropzone.DropzoneOptions = {
        addRemoveLinks: true,
        url: resourceApiUrl,
        paramName: "file",
        maxFilesize: maxFileSize,
        maxThumbnailFilesize: maxFileSize,
        maxFiles: maxFiles,
        uploadMultiple: false,
        parallelUploads: 10, // autoqueue doesn't work when multiple files are dropped
        acceptedFiles: acceptedFiles,
        dictDefaultMessage: defaultMessage,
        timeout: 1800000 // 30 minutes - to make sure anybody uploading larger video will be able to complete it
    };
    if (mode === "image") {
        dropZoneOptions.accept = (file: Dropzone.DropzoneFile & AcceptableFile, done) => {
            file.acceptFile = done;
            file.rejectFile = reason => {
                done(reason);
            };
        }
    }
    const dropzone = new Dropzone(uploaderEl[0], dropZoneOptions);
    replacePreviewTemplateThumbnailForNonImageModeByFontAwesomeIcon();
    
    function replacePreviewTemplateThumbnailForNonImageModeByFontAwesomeIcon() {
        if (mode !== "image" && dropzone.options.previewTemplate) {
            const dzImageElement = "<div class=\"dz-image\"><img data-dz-thumbnail /></div>";
            if (dropzone.options.previewTemplate.indexOf(dzImageElement) === -1) {
                throw new Error(`Cannot find substring ${dzImageElement} in dropzone preview template.`);
            }
            dropzone.options.previewTemplate = dropzone.options.previewTemplate.replace(
                dzImageElement,
                `<div class="dz-image text-center"><i class="far fa-file-${mode} fa-4x" style="color: lightgray"></i></div>`
            );
        }
    }

    function syncFileIds() {
        const newFiles = dropzone.files
            .filter((f) => f.status === Dropzone.SUCCESS && (f as FileInfo).serverFileId)
            .map((f): ServerFileInfo => ({
                Id: (f as FileInfo).serverFileId,
                Url: (f as FileInfo).serverFileUrl,
                Size: (f as FileInfo).serverFileSize,
            }));
        const files = [...existingFiles, ...newFiles];
        const fileOrder = sortable.toArray();
        files.sort((a, b) => fileOrder.indexOf(a.Id.toString()) - fileOrder.indexOf(b.Id.toString()));
        const objectToStringify = isFileCollection
            ? files
            : files.length > 0 ? files[0] : undefined
        fileInfoEl.val(JSON.stringify(objectToStringify));
    }

    const sortable = new Sortable(uploaderEl[0], {
        draggable: '.dz-preview',
        dragClass: 'border',
        dataIdAttr: 'data-file-id',
        store: {
            get: () => {
                return existingFiles.map(f => f.Id.toString());
            },
            set: () => {
                syncFileIds();
            }
        }
    });

    dropzone.on('addedfile', (file: FileInfo) => {
        if (mode === 'image') {
            const setCopyrightButton = Dropzone.createElement('<a class="dz-copyright" href="javascript:undefined;">Set Source</a>');

            setCopyrightButton.addEventListener('click', function (e) {
                // Make sure the button click doesn't submit the form:
                e.preventDefault();
                e.stopPropagation();

                if (!file.serverFileId) {
                    alert('Please wait until the file is fully uploaded to the server and retry.');
                    return;
                }

                const copyright = prompt('Set source (external source attribution e.g. Wikipedia):', file.copyright || '');
                if ((copyright || copyright === '') && copyright !== file.copyright) {

                    postRequestAndWaitForJobCompletion(
                        `${resourceApiUrl}/${file.serverFileId}/Copyright`,
                        { copyright },
                        jobApiUrl,
                        undefined,
                        () => {},
                        true,
                    );
                    file.copyright = copyright;
                }
            });

            // Add the button to the file preview element.
            file.previewElement.appendChild(setCopyrightButton);
            if (file.serverFileId) {
                file.previewElement.setAttribute('data-file-id', file.serverFileId.toString());
            }
        }
    });

    function dispatchFilesCountChangedEvent() {
        uploaderEl[0].dispatchEvent(new CustomEvent(filesCountChangedEventName, { bubbles: true, detail: dropzone.files.filter(x => x.accepted).length }));
    }

    dropzone.on('success', (file: FileInfo, response: string) => {
        if (response && response.length) {
            const jobId = response;
            file.status = Dropzone.UPLOADING;
            const previewElement = $(file.previewElement);
            disableElementAndAddSpinner(previewElement);
            waitForJobCompletion(jobApiUrl, jobId, previewElement, jobResult => {
                const resourceId = jobResult.affectedAggregateRootEntities.find(e => e.aggregateRootEntityName === aggregateRootEntityName)?.aggregateRootEntityId;
                if (!resourceId) {
                    throw new Error('Server upload failed - please retry.');
                }
                file.serverFileId = resourceId;
                file.status = Dropzone.SUCCESS;
                file.previewElement.setAttribute('data-file-id', file.serverFileId.toString());
                $.get(`/api/resource/GetResourceUrl/${resourceId}`, resourceUrl => {
                    file.serverFileUrl = resourceUrl;
                    syncFileIds();
                });

                dispatchFilesCountChangedEvent();
            }, 1)
        }
        else {
            dropzone.emit('error', file, response);
        }
    });

    function removeFakeFileFromDropzoneFiles(file: FileInfo) { // see function addFakeFileToDropzoneFilesToSimulateTheFileHasBeenAddedManually()
        const fakeFileIndex = dropzone.files.findIndex(x => x.name === file.name);
        if (fakeFileIndex !== -1) {
            dropzone.files.splice(fakeFileIndex, 1);
        }
    }

    dropzone.on('removedfile', (file: FileInfo) => {
        if (!file.serverFileId) return;

        if (file.existingFile === true) {
            existingFiles = existingFiles.filter(f => f.Id !== file.serverFileId);
        }

        removeFakeFileFromDropzoneFiles(file);

        syncFileIds();

        dispatchFilesCountChangedEvent();

        // TODO: delete file instance on server
        /*
        $.ajax({
            url: `${resourceApiUrl}/${file.serverFileId}`,
            method: 'DELETE',
            success: syncFileIds,
            error: () => {
                dropzone.emit('error', file, 'failed to remove uploaded file from the server');
                //should we somehow add the file back?
                //dropzone.addFile(file);
                syncFileIds();
            }
        });*/
    });

    dropzone.on('thumbnail', (file: FileInfo & AcceptableFile & Dimensions) => {
        if (file.existingFile || file.status === "error") {
            return;
        }
        if (mode !== 'image') {
            file.acceptFile();
            return;
        }
        if (file.width > maxImageWidth || file.height > maxImageHeight) {
            file.rejectFile(`Please upload smaller image - maximum image size is ${maxImageWidth} x ${maxImageHeight}px but uploaded image is ${file.width} x ${file.height}px.`);
            return;
        }
        if (file.width < minImageWidth || file.height < minImageHeight) {
            file.rejectFile(`Please upload larger image - minimum image size is ${minImageWidth} x ${minImageHeight}px but uploaded image is ${file.width} x ${file.height}px.`);
            return;
        }
        if ((file.width > file.height && file.width / file.height > 2)
            || (file.width < file.height && file.height / file.width > 2))
        {
            const aspectRatio = file.width > file.height
                ? `${(file.width / file.height).toFixed(2)}:1`
                : `1:${(file.height / file.width).toFixed(2)}`
            file.rejectFile(
                `The image is too wide, or too thin - the image aspect ratio ${aspectRatio} is invalid. ` +
                `The recommended aspect ratio is 4:3 for landscape and 3:4 for portrait. ` +
                `The tolerated aspect ratio is at 2:1 for landscape and 1:2 for portrait.`);
            return;
        }

        file.acceptFile();
    });

    // more info about why adding a fake file is needed: https://github.com/dropzone/dropzone/issues/443#issuecomment-905357585
    function addFakeFileToDropzoneFilesToSimulateTheFileHasBeenAddedManually(f: ServerFileInfo) {
        const fakeFile = {
            name: f.Id.toString(),
            accepted: true
        };
        // @ts-ignore
        dropzone.files.push(fakeFile);
    }

    existingFiles.forEach(f => {
        dropzone.displayExistingFile({ name: f.Id.toString(), size: f.Size || 1000000, serverFileId: f.Id, copyright: f.Copyright, existingFile: true }, f.Url || '', () => { }, 'anonymous');
        addFakeFileToDropzoneFilesToSimulateTheFileHasBeenAddedManually(f);
    });

    dropzone.on('error', (file: FileInfo, response: string, xhr: XMLHttpRequest) => {
        if (xhr !== undefined && xhr.status === 400 && response.includes("Request body too large")) {
            var errorMessage;
            if (file.type.startsWith('video')) {
                errorMessage = `The video is too large, the limit is ${ResourceConstants.videoFileSizeLimit / 1000000}MB. Please convert it to a smaller size, for instance using <a href="https://video-converter.com" target="_blank">video-converter.com</a>.`
            } else if (file.type.startsWith('image')) {
                errorMessage = `The photo is too large, the limit is ${ResourceConstants.imageFileSizeLimit / 1000000}MB. Please resize it to make it smaller, for instance using <a href="https://www.reduceimages.com/" target="_blank">reduceimages.com</a>.`
            } else if (file.type.startsWith('audio')) {
                errorMessage = `The audio is too large, the limit is ${ResourceConstants.audioFileSizeLimit / 1000000}MB. Please convert it to a smaller size, for instance using <a href="https://online-audio-converter.com/" target="_blank">online-audio-converter.com</a>.`
            } else {
                errorMessage = JSON.parse(response).errors[""][0];
            }

            file.previewElement.querySelectorAll('.dz-error-message span')[0].innerHTML = errorMessage;
        }
    });
}
